"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.InlineState = void 0;
const ui_1 = require("@jsonjoy.com/ui");
const slate_react_1 = require("slate-react");
const InlineMenu_1 = require("./InlineMenu");
const LinkButtonState_1 = require("./link/LinkButtonState");
const InlineMathState_1 = require("./InlineMathState");
const TOOLBAR_HEIGHT = 36;
const GAP = 8;
const TOOLBAR_VIEWPORT_OVERFLOW_LIMIT = 50;
class InlineState {
    constructor(mutxt, scrollArea) {
        this.mutxt = mutxt;
        this.scrollArea = scrollArea;
        this.pointerDownOutsideToolbar = ui_1.rsync.val(false);
        this.dismissed = ui_1.rsync.val(false);
        this.isFocusInToolbar = null;
        this.popupOpen = ui_1.rsync.val(false);
        this.setPopupOpen = (open) => {
            this.popupOpen.set(open);
        };
        this.recentColors = ui_1.rsync.val([]);
        this.pushRecentColor = (color) => {
            if (!color)
                return;
            const list = this.recentColors.value.filter((c) => c !== color);
            list.unshift(color);
            if (list.length > 10)
                list.length = 10;
            this.recentColors.next(list);
        };
        this.start = () => {
            const mutxt = this.mutxt;
            const unsubscribeSelection = mutxt.selection.subscribe(() => {
                if (mutxt.selection.value)
                    this.dismissed.next(false);
            });
            // Editor-blur dismiss has to be deferred to a microtask. Slate's onBlur
            // fires synchronously inside the mousedown that's currently shifting
            // focus to whatever the user clicked — at that moment, the focusin event
            // for the new target hasn't yet updated our `isFocusInToolbar` check, so
            // a synchronous dismiss would unmount the toolbar before the click on its
            // own button can register. Deferring lets the focusin handler run first.
            const unsubscribeFocused = mutxt.focused.subscribe(() => {
                if (mutxt.focused.value)
                    return;
                queueMicrotask(() => {
                    if (mutxt.focused.value)
                        return;
                    if (this.isFocusInToolbar?.())
                        return;
                    if (this.popupOpen.value)
                        return;
                    this.dismissed.next(true);
                });
            });
            return () => {
                unsubscribeSelection();
                unsubscribeFocused();
            };
        };
        this.menu = new InlineMenu_1.InlineMenu(mutxt);
        this.link = new LinkButtonState_1.LinkButtonState(mutxt);
        this.math = new InlineMathState_1.InlineMathState(mutxt);
    }
    /** Returns true when the supplied anchor point is within (or no more than
     *  TOOLBAR_VIEWPORT_OVERFLOW_LIMIT pixels outside of) the ScrollArea
     *  viewport. When no ScrollArea is registered, always returns true. */
    isToolbarWithinViewport(point, toolbarElement) {
        if (!this.scrollArea?.viewportEl)
            return true;
        if (typeof window === 'undefined' || typeof document === 'undefined')
            return true;
        try {
            const viewportRect = this.scrollArea.viewportEl.getBoundingClientRect();
            const toolbarHeight = toolbarElement?.getBoundingClientRect().height || TOOLBAR_HEIGHT;
            const top = point.dy < 0 ? point.y - toolbarHeight : point.y;
            const bottom = point.dy < 0 ? point.y : point.y + toolbarHeight;
            const topOverflow = Math.max(viewportRect.top - top, 0);
            const bottomOverflow = Math.max(bottom - viewportRect.bottom, 0);
            return topOverflow <= TOOLBAR_VIEWPORT_OVERFLOW_LIMIT && bottomOverflow <= TOOLBAR_VIEWPORT_OVERFLOW_LIMIT;
        }
        catch {
            return false;
        }
    }
    anchorPoint() {
        const mutxt = this.mutxt;
        const selection = mutxt.selection.value;
        if (!selection)
            return;
        try {
            const domRange = slate_react_1.ReactEditor.toDOMRange(mutxt.editor, selection);
            const selectionRect = domRange.getBoundingClientRect();
            const focusRect = this.getFocusCaretRect() ?? selectionRect;
            // Reject degenerate rects. They appear briefly while a mark is being
            // applied (e.g. Bold wraps the affected text in a `<strong>` element):
            // for one render the DOM range references nodes that are being
            // re-mounted, so getBoundingClientRect() returns 0,0,0,0. Returning
            // undefined here lets the caller fall back to the cached point instead
            // of placing the toolbar at the page origin and triggering the
            // viewport-overflow check.
            if (!focusRect.width && !focusRect.height && !focusRect.top && !focusRect.left)
                return;
            const x = focusRect.width > 0 ? focusRect.left + focusRect.width / 2 : focusRect.left;
            if (focusRect.top < TOOLBAR_HEIGHT + GAP)
                return { x, y: focusRect.bottom + GAP, dx: 0, dy: 1 };
            return { x, y: focusRect.top - GAP, dx: 0, dy: -1 };
        }
        catch {
            return;
        }
    }
    getFocusCaretRect() {
        const nativeSelection = window.getSelection();
        if (!nativeSelection?.focusNode)
            return null;
        try {
            const focusRange = document.createRange();
            focusRange.setStart(nativeSelection.focusNode, nativeSelection.focusOffset);
            focusRange.collapse(true);
            const rect = focusRange.getClientRects()[0] ?? focusRange.getBoundingClientRect();
            if (rect.width > 0 || rect.height > 0)
                return rect;
            return null;
        }
        catch {
            return null;
        }
    }
}
exports.InlineState = InlineState;
